import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, CircleCheck, Receipt, CircleDollarSign } from "lucide-react";
import api, { formatErr, API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";
import { bumpUnpaidInvoices } from "../lib/unpaidInvoices";
import CustomerPinPromptDialog from "../components/customer/CustomerPinPromptDialog";
import { deDateMedium, deDateTime } from "../lib/dateFormat";

function StatusBadge({ status, de }) {
  const map = {
    paid:    { label: de ? "Bezahlt" : "Paid", bg: STATUS_COLORS.green, fg: "#fff" },
    sent:    { label: de ? "Offen" : "Open",   bg: BRAND_COLORS.gold, fg: BRAND_COLORS.navy },
    overdue: { label: de ? "Überfällig" : "Overdue", bg: STATUS_COLORS.red, fg: "#fff" },
    draft:   { label: de ? "Entwurf" : "Draft", bg: "#e5e7eb", fg: "#374151" },
  };
  const s = map[status] || map.sent;
  return (
    <span data-testid={`invoice-status-${status}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] tracking-wider uppercase rounded-full shrink-0"
      style={{ background: s.bg, color: s.fg }}>
      {status === "paid" && <CircleCheck className="w-3 h-3" strokeWidth={2} />}
      {s.label}
    </span>
  );
}

export default function CustomerInvoices() {
  const { lang } = useI18n();
  const de = lang === "de";
  const fmtDate = (iso) => iso ? deDateMedium(iso) : "";
  const fmtMoney = (n, cur = "EUR") => `${deDateTime((n || 0))} ${cur}`;

  const [rows, setRows] = useState(null);
  // Feb 2026 · 9 Sep 2026 · User-Directive „Wenn ein Kunde eine Rechnung
  // als bezahlt markieren möchte, soll er auf die Rechnung klicken und
  // seinen 4-stelligen PIN eingeben". `payTarget` hält die aktuell
  // markierte Rechnung; der `CustomerPinPromptDialog` läuft im
  // `confirm`-Modus und ruft `PATCH /invoices/{id}/customer-pay` mit
  // der eingegebenen PIN auf.
  const [payTarget, setPayTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/invoices");
      setRows(data);
      try { await api.post("/invoices/mark-read"); } catch { /* silent */ }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const canPay = (inv) =>
    inv && inv.status !== "paid" && inv.status !== "draft" && inv.status !== "cancelled" && inv.type !== "credit_note";

  const submitPay = async (pin) => {
    if (!payTarget) return;
    try {
      await api.patch(`/invoices/${payTarget.id}/customer-pay`, { pin });
      toast.success(de ? "Rechnung als bezahlt markiert." : "Invoice marked as paid.");
      setPayTarget(null);
      bumpUnpaidInvoices();
      await load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        // PIN falsch — Dialog offen halten, damit der Kunde erneut versucht.
        toast.error(de ? "PIN ist falsch. Bitte erneut versuchen." : "PIN incorrect. Please try again.");
        throw err;
      }
      if (status === 428) {
        toast.error(de
          ? "Bitte richten Sie zuerst Ihre PIN unter Einstellungen ein."
          : "Please set up your PIN in Settings first.");
        setPayTarget(null);
        return;
      }
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-10 py-8 md:py-14 pb-32" data-testid="customer-invoices">
      <div className="mb-8">
        <div className="label-caps mb-3">{de ? "Ihr Objekt" : "Your property"}</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy leading-tight">
          {de ? "Rechnungen" : "Invoices"}
        </h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-lg">
          {de
            ? "Alle ausgestellten Rechnungen im Überblick — mit PDF zum Download. Klicken Sie auf eine offene Rechnung, um sie mit Ihrer PIN als bezahlt zu bestätigen."
            : "All issued invoices at a glance — with downloadable PDF. Tap an open invoice to confirm payment with your PIN."}
        </p>
      </div>

      {rows === null ? (
        <div className="bg-white border border-navy/10 p-8 text-center label-caps">
          {de ? "Wird geladen …" : "Loading …"}
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-ivory/60 border border-dashed border-navy/20 p-10 text-center" data-testid="invoices-empty">
          <Receipt className="w-8 h-8 mx-auto mb-3" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.3} />
          <div className="font-display text-xl text-navy">
            {de ? "Noch keine Rechnungen" : "No invoices yet"}
          </div>
          <p className="text-[13px] text-navy-dark/60 mt-2 max-w-xs mx-auto">
            {de
              ? "Sobald Ihr Concierge-Team eine Rechnung ausstellt, erscheint sie hier."
              : "As soon as your concierge team issues an invoice it will appear here."}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="invoices-list">
          {rows.map((inv) => {
            const payable = canPay(inv);
            return (
              <li
                key={inv.id}
                className={`bg-white border p-4 md:p-5 flex items-center gap-3 md:gap-5 transition-colors ${
                  payable ? "border-navy/10 hover:border-gold cursor-pointer" : "border-navy/10"
                }`}
                data-testid={`invoice-row-${inv.id}`}
                data-payable={payable ? "true" : "false"}
                onClick={payable ? () => setPayTarget(inv) : undefined}
                onKeyDown={(e) => {
                  if (!payable) return;
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPayTarget(inv); }
                }}
                role={payable ? "button" : undefined}
                tabIndex={payable ? 0 : undefined}
              >
                <div className="w-11 h-11 rounded-full bg-ivory flex items-center justify-center shrink-0">
                  <Receipt className="w-4.5 h-4.5 text-navy-dark/70" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg text-navy leading-tight">{inv.title}</div>
                    <StatusBadge status={inv.status} de={de} />
                    {inv.paid_by_customer && inv.status === "paid" && (
                      <span
                        data-testid={`invoice-paid-by-customer-${inv.id}`}
                        className="text-[10px] tracking-[0.12em] uppercase text-navy-dark/60"
                      >
                        {de ? "durch PIN bestätigt" : "confirmed via PIN"}
                      </span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-navy-dark/60 mt-1 truncate">
                    {inv.number} · {fmtDate(inv.issued_at)}{inv.due_date ? ` · ${de ? "fällig" : "due"} ${fmtDate(inv.due_date)}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0 flex flex-col items-end gap-2">
                  <div className="font-display text-lg md:text-xl text-navy leading-tight">{fmtMoney(inv.total, inv.currency)}</div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <a
                      href={`${API}/invoices/${inv.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`invoice-pdf-${inv.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-1.5"
                    >
                      <FileDown className="w-3.5 h-3.5" strokeWidth={1.5} /> PDF
                    </a>
                    {payable && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPayTarget(inv); }}
                        data-testid={`invoice-pay-btn-${inv.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-ivory hover:opacity-90 transition-opacity px-3 py-1.5"
                        style={{ background: BRAND_COLORS.navy }}
                      >
                        <CircleDollarSign className="w-3.5 h-3.5" strokeWidth={1.6} />
                        {de ? "Bezahlt" : "Paid"}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <CustomerPinPromptDialog
        open={!!payTarget}
        mode="confirm"
        de={de}
        title={de ? "Zahlung bestätigen" : "Confirm payment"}
        description={payTarget
          ? (de
              ? `Bitte geben Sie Ihre 4-stellige PIN ein, um die Rechnung ${payTarget.number || ""} über ${fmtMoney(payTarget.total, payTarget.currency)} als bezahlt zu markieren.`
              : `Enter your 4-digit PIN to mark invoice ${payTarget.number || ""} for ${fmtMoney(payTarget.total, payTarget.currency)} as paid.`)
          : undefined}
        submitLabel={de ? "Als bezahlt markieren" : "Mark as paid"}
        onClose={() => setPayTarget(null)}
        onConfirm={submitPay}
      />
    </div>
  );
}
